import { BossBullet } from '../../entities/BossBullet.js';

const INITIAL_SIZE = 256; // bullet-hell patterns can burst well past the player's own pool size

/**
 * Object pool for boss/minion bullets — mirrors combat/ProjectilePool's
 * design (see its comment for why active projectiles are kept packed into
 * `_all[0, _activeCount)` via swap-on-deactivate, instead of just scanning
 * a backing array that only ever grows to match a run's peak concurrent
 * bullet count and never shrinks back down).
 */
export class BulletPool {
  constructor(initialSize = INITIAL_SIZE) {
    this._all = Array.from({ length: initialSize }, () => new BossBullet());
    this._activeCount = 0;
    // Whatever is currently firing into this pool — a boss pattern's name, or
    // a minion's type id. Set by the system that owns the pool immediately
    // before it hands the pool to a pattern/minion (see SpawningSystem and
    // MinionWaveSystem), so every fire() below stamps its bullet with the
    // right origin without a single pattern having to pass it. That stamp is
    // what lets the death recap name what actually killed you, long after the
    // pattern that fired the bullet has finished and been discarded.
    this.sourceLabel = null;
    // Monotonic per-label count of everything ever fired through this pool.
    // Spell-card capture (BULLET-HELL.md §D1) prices a card by how many
    // bullets it actually put on screen, which is only knowable at fire time —
    // active-count deltas can't see it, since bullets expire while the pattern
    // is still firing. Never reset: SpawningSystem reads it as a difference
    // between two snapshots, so drift is impossible and there is nothing to
    // clear between cards.
    this.fireCounts = new Map();
    // Tine's Attunement (BULLET-HELL.md §D7's champion promotion): a stamp
    // applied to every bullet the instant it spawns, which tones and recolours
    // it (systems/combat/attunement.js). Null for every other champion, so the
    // roster that doesn't tone anything pays one null check per bullet. Set by
    // Game as each SpawningSystem is built, because the champion is a run-level
    // fact and the pool is per-fight.
    this.toneCaster = null;
    // Per-bullet spawn patch for the pattern currently firing (BOSS-REWORK.md
    // §B10). A mastery variant can say "this card's bullets curve now" without
    // the pattern class knowing a variant exists — SpawningSystem sets this
    // beside `sourceLabel` as it hands the pool to each pattern, and clears it
    // the same way. Null for every unvaried card, i.e. almost always.
    this.spawnOverlay = null;
  }

  /** Bullets ever fired under `label` — see `fireCounts`. */
  firedUnder(label) {
    return this.fireCounts.get(label) ?? 0;
  }

  fire(config) {
    let bullet;
    if (this._activeCount < this._all.length) {
      bullet = this._all[this._activeCount];
    } else {
      bullet = new BossBullet();
      this._all.push(bullet);
    }
    this._activeCount++;
    bullet.spawn(this.spawnOverlay ? { ...config, ...this.spawnOverlay } : config);
    // After spawn(), never inside it: spawn() is the reset, and a tone written
    // before it would be wiped by the very next recycle of this bullet.
    if (this.toneCaster) this.toneCaster(bullet);
    bullet.source = this.sourceLabel;
    if (this.sourceLabel) {
      this.fireCounts.set(this.sourceLabel, (this.fireCounts.get(this.sourceLabel) ?? 0) + 1);
    }
    return bullet;
  }

  /**
   * Resolves one bullet's sub-munition split (BOSS-REWORK.md §B3). The
   * children inherit the parent's damage, colour and tone and are dealt across
   * `splitSpread` centred on the parent's heading, so one legible bullet
   * becomes a fan at a moment the player could see coming.
   *
   * The parent is consumed unless it asked not to be: §B3's constraint is that
   * splits come OUT of a pattern's bullet budget, not on top of it.
   *
   * Fired through this.fire(), so children carry the same source stamp and
   * count toward the card's danger proxy like any other bullet — a card that
   * splits is priced for what it actually put on screen.
   */
  _resolveSplit(parent) {
    parent.splitPending = false;
    const count = parent.splitInto;
    const speed = (parent.speed || Math.hypot(parent.vx, parent.vy)) * parent.splitSpeedMultiplier;
    const base = Math.atan2(parent.vy, parent.vx);
    const spread = parent.splitSpread;
    for (let i = 0; i < count; i++) {
      // A single child inherits the heading exactly; more than one fans out
      // evenly across the spread, symmetric about the parent's course.
      const t = count === 1 ? 0.5 : i / (count - 1);
      const angle = base - spread / 2 + spread * t;
      this.fire({
        x: parent.x,
        y: parent.y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        radius: Math.max(2, parent.radius * 0.72),
        damage: parent.damage,
        color: parent.color,
        lifetime: Math.max(0.6, parent.lifetime),
        tone: parent.tone,
        mismatchBonus: parent.mismatchBonus,
      });
    }
    if (!parent.splitKeepsParent) parent.active = false;
    else parent.splitAfter = 0; // a surviving parent splits once, not every frame
  }

  update(dt) {
    const splitting = [];
    for (let i = 0; i < this._activeCount; i++) {
      const b = this._all[i];
      b.update(dt);
      if (b.splitPending) splitting.push(b);
      if (!b.active) {
        this._activeCount--;
        this._all[i] = this._all[this._activeCount];
        this._all[this._activeCount] = b;
        i--;
      }
    }

    // Splits resolve AFTER the movement sweep, so a child never takes a tick
    // of travel on the frame it was born and the fan leaves its parent's
    // position cleanly. The source stamp is restored per parent because the
    // pool's live label is null out here (update runs outside any pattern's
    // turn) and the children would otherwise be unattributed.
    for (const parent of splitting) {
      if (!parent.active) continue; // expired on the same frame it was due to split
      const previousLabel = this.sourceLabel;
      this.sourceLabel = parent.source;
      this._resolveSplit(parent);
      this.sourceLabel = previousLabel;
      if (!parent.active) this._deactivate(parent);
    }
  }

  /** Packs a bullet that died outside the update sweep back out of the active range. */
  _deactivate(bullet) {
    const i = this._all.indexOf(bullet);
    if (i === -1 || i >= this._activeCount) return;
    this._activeCount--;
    this._all[i] = this._all[this._activeCount];
    this._all[this._activeCount] = bullet;
  }

  forEachActive(fn) {
    for (let i = 0; i < this._activeCount; i++) {
      // Still defensively checked — see ProjectilePool.forEachActive's comment.
      const b = this._all[i];
      if (b.active) fn(b);
    }
  }

  get activeCount() {
    return this._activeCount;
  }
}
